// Snapshot-based undo/redo. Pure operations on (past, future) tuples.
// `state.ts` wires these to the live store.

import type { AppState } from './types';

export const MAX_HISTORY = 200;

export type HistoryStacks = {
  past: AppState[];
  future: AppState[];
};

export function emptyHistory(): HistoryStacks {
  return { past: [], future: [] };
}

/** Push a "before-change" snapshot. Clears the redo stack. */
export function push(h: HistoryStacks, snapshot: AppState): HistoryStacks {
  const past = h.past.length >= MAX_HISTORY
    ? [...h.past.slice(1), snapshot]
    : [...h.past, snapshot];
  return { past, future: [] };
}

export type UndoResult = { state: AppState; history: HistoryStacks } | null;

export function undo(h: HistoryStacks, current: AppState): UndoResult {
  if (h.past.length === 0) return null;
  const prev = h.past[h.past.length - 1] as AppState;
  return {
    state: prev,
    history: {
      past: h.past.slice(0, -1),
      future: [current, ...h.future],
    },
  };
}

export function redo(h: HistoryStacks, current: AppState): UndoResult {
  if (h.future.length === 0) return null;
  const next = h.future[0] as AppState;
  return {
    state: next,
    history: {
      past: [...h.past, current],
      future: h.future.slice(1),
    },
  };
}

export function clear(): HistoryStacks {
  return emptyHistory();
}

export function canUndo(h: HistoryStacks): boolean {
  return h.past.length > 0;
}

export function canRedo(h: HistoryStacks): boolean {
  return h.future.length > 0;
}
